import { Canvas, useFrame } from "@react-three/fiber";
import { Suspense, useRef, useMemo } from "react";
import * as THREE from "three";

function WireIcosahedron({ scale = 1.8, color = "#86EFAC", speed = 1 }) {
  const ref = useRef();
  useFrame((state, delta) => {
    if (!ref.current) return;
    ref.current.rotation.x += delta * 0.25 * speed;
    ref.current.rotation.y += delta * 0.35 * speed;
  });
  return (
    <group ref={ref}>
      <mesh scale={scale}>
        <icosahedronGeometry args={[1, 1]} />
        <meshBasicMaterial wireframe color={color} transparent opacity={0.55} />
      </mesh>
      <mesh scale={scale * 0.6}>
        <octahedronGeometry args={[1, 0]} />
        <meshBasicMaterial wireframe color="#22D3EE" transparent opacity={0.4} />
      </mesh>
    </group>
  );
}

function Particles({ count = 220 }) {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 10;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 6;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 6;
    }
    return arr;
  }, [count]);
  useFrame((s, d) => {
    if (ref.current) ref.current.rotation.y += d * 0.05;
  });
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial size={0.02} color="#86EFAC" transparent opacity={0.7} />
    </points>
  );
}

/**
 * Full-width techie divider — wireframe + particles.
 * Drop between sections for visual rhythm.
 */
export default function TechDivider({ variant = "gold", label = "SYSTEM ONLINE" }) {
  const color = variant === "cyan" ? "#22D3EE" : "#86EFAC";
  return (
    <section
      data-testid={`tech-divider-${variant}`}
      className="relative h-[340px] lg:h-[420px] w-full overflow-hidden border-y border-nova-line tech-grid"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-nova-bg via-transparent to-nova-bg pointer-events-none z-10" />

      {/* orbs */}
      <div className={`orb ${variant === "cyan" ? "orb-cyan" : "orb-gold"} w-[420px] h-[420px] -top-40 left-1/3`} />
      <div className="orb orb-violet w-[320px] h-[320px] -bottom-32 right-1/4" />

      {/* 3D */}
      <div className="absolute inset-0 z-0">
        <Canvas dpr={[1, 1.5]} camera={{ position: [0, 0, 6], fov: 45 }} gl={{ alpha: true, antialias: true }}>
          <ambientLight intensity={0.4} />
          <Suspense fallback={null}>
            <WireIcosahedron color={color} />
            <Particles />
          </Suspense>
        </Canvas>
      </div>

      {/* Overlay labels */}
      <div className="relative z-20 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-between py-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="mono-cyan">{label}</span>
          </div>
          <span className="mono-label opacity-70">v1.0.2 · STABLE</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 font-mono text-[10px] uppercase tracking-widest text-nova-mute">
          <div>lat: 24.9°N</div>
          <div>lon: 79.5°E</div>
          <div>uptime: 99.98%</div>
          <div className="text-nova-gold">status: shipping</div>
        </div>
      </div>
    </section>
  );
}
